import { Component, forwardRef, Input, ViewChild } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { TimeModel } from '../../models/time.model';
import { timeFormatsEnum } from '../../../../Core/Users/enums/time-formats.enum';
import { meridiansEnum } from '../../enums/meridians.enum';
import { DeliveryRangeModel } from '../../models/delivery-range.model';
import moment from 'moment-timezone';
import { TimeHelper } from '../../helpers/time.helper';
import { filter, Subscription } from 'rxjs';
import { CurrentUserStateService } from '../../../../Core/Users/services/current-user-state.service';

const deliveryRangeSchedulerValueAccessor: any = {
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => DeliveryRangeSchedulerComponent),
    multi: true
};


/**
 * ### Accessibility Usage
 * + ***ariaDescribedBy*** - use this if the component has an external error message, or it needs extra description for screen reader. Use message's id
 * + ***id*** - provide an unique id so that this component can be correctly recognized by screen readers. Use interpolation [id]="'unique_id'"
 */
@Component({
    selector: 'agpr-delivery-range-scheduler',
    templateUrl: './delivery-range-scheduler.component.html',
    styleUrls: ['./delivery-range-scheduler.component.scss'],
    providers: [deliveryRangeSchedulerValueAccessor],
    standalone: false
})
export class DeliveryRangeSchedulerComponent implements ControlValueAccessor {
    @Input() id: string = null;

    /** Can the "ongoing" option be selected */
    @Input() canSelectOngoing: boolean = true;
    @Input() showOngoingTooltip: boolean = true;
    /** Handle disabling this field */
    @Input() disabled: boolean = false;
    /** Minimum start date */
    @Input() minStartDate: Date;
    /**
     * Maximum start date
     * @NOTE: if the max start date is greater than the max end date, then the max end date will be used
     * @NOTE: once the end date has been set, it will also be taken into acount
     */
    @Input() maxStartDate: Date;
    /**
     * Minimum end date
     * @NOTE: if the min end date is less than the min start date, then the min start date will be used
     * @NOTE: once the start date has been set, it will also be taken into acount
     */
    @Input() minEndDate: Date;
    /** Maximum end date */
    @Input() maxEndDate: Date;

    @Input() startLabel: string = 'DeliveryRangeScheduler.start';
    @Input() endLabel: string = 'DeliveryRangeScheduler.end';
    @Input() timePeriodLabel: string = 'DeliveryRangeScheduler.timePeriod';

    @Input() ariaDescribedBy: string;

    range: DeliveryRangeModel;

    rangeTypeOngoing: number = 0;
    rangeTypeRange: number = 1;
    selectedRangeType: number = 0;

    startDate: Date;
    startTime: TimeModel;
    realMaxStartDate: Date;

    endDate: Date;
    endTime: TimeModel;
    realMinEndDate: Date;

    timezone: string;

    private endDateSelected: boolean = false;

    subscriptions: Subscription[] = [];
    timeFormat: timeFormatsEnum;

    @ViewChild('startCalendar') startCalendar: any;
    @ViewChild('endCalendar') endCalendar: any;

    constructor(public timeHelper: TimeHelper,
        public currentUserStateService: CurrentUserStateService
    ) {
        this.subscriptions.push(
            this.currentUserStateService.getPreferences().pipe(filter(x => !!x)).subscribe(prefs => {
                this.timeFormat = prefs.timeFormat;
                this.startTime = this.getDefaultTimeModel();
                this.endTime = this.getDefaultTimeModel();
            })
        );
    }

    onModelChange: Function = () => {};
    onModelTouched: Function = () => {};

    /**
     * Recieves a value from the parent component via ngModel input
    */
    writeValue(value: DeliveryRangeModel): void {
        this.range = value;

        if (!value) {
            this.range = {
                ongoing: true,
                start: null,
                end: null,
                timezone: null
            };
            this.resetFieldsToOngoing();
            return;
        }

        // it's ongoing unless it has explicitly been set to range (null/undefined will be ongoing)
        this.selectedRangeType = value.ongoing === false ? this.rangeTypeRange : this.rangeTypeOngoing;

        // reset the values to their defaults when we get changes
        this.resetFieldsToOngoing();

        // set each of the values that we have data for
        if (!value.ongoing) {
            if (value.start) {
                this.startDate = new Date(value.start);
                this.startTime = this.getTimeModelFromDate(this.startDate);
            }
            if (value.end) {
                this.endDate = new Date(value.end);
                this.endTime = this.getTimeModelFromDate(this.endDate);
            }
        }
        if (value.timezone) {
            this.timezone = value.timezone;
        } else {
            this.timezone = null;
        }

        this.updateMinMax();
    }

    /**
     * Updates the min end date and the max start date based on the current values of start and end
     */
    updateMinMax(): void {
        let minDate = null;
        // Get the minimum date from the inputs
        if (this.minEndDate) {
            if (this.minStartDate) {
                // Verify that the min end date is greater than the min start date
                minDate = Math.max(+this.minEndDate, +this.minStartDate);
            } else {
                minDate = +this.minEndDate;
            }
        } else if (this.minStartDate) {
            // if the min end date is not set, but the min start date is, then use min start date
            minDate = +this.minStartDate;
        }
        // Once the start date has been set, the min end date should never be less than that, so accomodate that as well
        if (minDate && this.startDate) {
            this.realMinEndDate = new Date(Math.max(minDate, +this.startDate));
        } else if (minDate) {
            this.realMinEndDate = new Date(minDate);
        } else if (this.startDate) {
            this.realMinEndDate = new Date(this.startDate);
        } else {
            this.realMinEndDate = null;
        }

        let maxDate = null;
        // Get the maximum date from the inputs
        if (this.maxStartDate) {
            if (this.maxEndDate) {
                // Verify that the max start date is less than the max end date
                maxDate = Math.min(+this.maxStartDate, +this.maxEndDate);
            } else {
                maxDate = +this.maxStartDate;
            }
        } else if (this.maxEndDate) {
            // if the max start date is not set, but the max end date is, then use max end date
            maxDate = +this.maxEndDate;
        }
        // Once the end date has been set, the max start date should never be less than that, so accomodate that as well
        if (maxDate && this.endDate) {
            this.realMaxStartDate = new Date(Math.max(+maxDate, +this.endDate));
        } else if (maxDate) {
            this.realMaxStartDate = new Date(maxDate);
        } else if (this.endDate) {
            this.realMaxStartDate = new Date(this.endDate);
        } else {
            this.realMaxStartDate = null;
        }

        // reset real max start Date to null if the user hasn't select any end date yet otherwise the selection of start date will be limited
        if (!this.endDateSelected){
            this.realMaxStartDate = null;
        }
    }

    /**
     * Registers the onModelChange function for when the value changes (broadcasts to ngModelChange)
     */
    registerOnChange(fn: Function): void {
        this.onModelChange = fn;
    }

    /**
     * Registers the onModelTouched function for when the value is touched, even if it does not change
    */
    registerOnTouched(fn: Function): void {
        this.onModelTouched = fn;
    }

    /**
     * Gets a time model from a date object
     */
    getTimeModelFromDate(date: Date): TimeModel {
        return this.timeHelper.getTimeFromDate(date, null, this.timeFormat);
    }

    /**
     * Gets a default time model
     */
    getDefaultTimeModel(): TimeModel {
        return new TimeModel(this.timeFormat);
    }

    /**
     * Resets the fields to their default values
     */
    resetFieldsToOngoing(): void {
        this.startDate = null;
        this.startTime = this.getDefaultTimeModel();
        this.endDate = null;
        this.endTime = this.getDefaultTimeModel();
    }

    /**
     * Handles the user selecting the "Ongoing" radio button
     */
    ongoingSelected(): void {
        this.range = {
            ongoing: true,
            start: null,
            end: null,
            timezone: this.range ? this.range.timezone : null // need to keep the timezone
        };
        this.resetFieldsToOngoing();

        this.onModelChange(this.range);
    }

    /**
     * Handles when the user selects the "Time Period" radio button
     */
    rangeSelected(): void {
        this.range.ongoing = false;

        // When you select range, default the start time to now
        const now = moment().tz(this.range.timezone);

        this.range.start = now.toDate();

        this.startDate = new Date(this.range.start);
        this.startTime = this.getTimeModelFromDate(this.startDate);

        this.onModelChange(this.range);
    }

    /**
     * Opens the calendar popup
     */
    openCalendar(event, calendar): void {
        calendar.hideOverlay();
        calendar.showOverlay();
        event.stopPropagation();
    }

    /**
     * Handles the start date value changing
     */
    startDateChanged(): void {
        if (!this.startDate) {
            this.startDate = new Date(this.range.start);
        }
        if (this.range.start) {
            this.range.start.setFullYear(this.startDate.getFullYear());
            this.range.start.setMonth(this.startDate.getMonth());
            this.range.start.setDate(this.startDate.getDate());
        } else {
            this.startDate.setHours(0);
            this.startDate.setMinutes(0);
            this.range.start = this.startDate;
            this.startTime.hour = 12;
            this.startTime.minute = 0;
            this.startTime.meridian = meridiansEnum.AM;
        }
        this.updateMinMax();

        this.onModelChange(this.range);
    }

    /**
     * Handles the start time value changing
     */
    startTimeChanged(): void {
        let hour: number = this.startTime.hour;
        const minute: number = this.startTime.minute;

        if (isNaN(hour) || isNaN(minute)) {
            this.startTime = this.getTimeModelFromDate(this.range.start);
            return;
        }

        if (hour === 12) {
            if (this.startTime.meridian === meridiansEnum.AM) {
                hour = 0;
            }
        } else {
            hour = this.startTime.meridian === meridiansEnum.PM ? hour + 12 : hour;
        }

        if (this.range.start) {
            this.range.start.setHours(hour);
            this.range.start.setMinutes(minute);
        } else {
            this.range.start = new Date();
            this.range.start.setHours(hour);
            this.range.start.setMinutes(minute);
            this.startDate = new Date(this.range.start);
        }

        this.onModelChange(this.range);
    }

    /**
     * Handles the end date value changing
     */
    endDateChanged(): void {
        if (!this.endDate) {
            this.endDate = new Date(this.range.end);
        }
        if (this.range.end) {
            this.range.end.setFullYear(this.endDate.getFullYear());
            this.range.end.setMonth(this.endDate.getMonth());
            this.range.end.setDate(this.endDate.getDate());
        } else {
            this.endDate.setHours(0);
            this.endDate.setMinutes(0);
            this.range.end = this.endDate;
            this.endTime.hour = 12;
            this.endTime.minute = 0;
            this.endTime.meridian = meridiansEnum.AM;
        }
        this.endDateSelected = true;
        this.updateMinMax();
        this.onModelChange(this.range);
    }

    /**
     * Handles the end time value changing
     */
    endTimeChanged(): void {
        let hour: number = this.endTime.hour;
        const minute: number = this.endTime.minute;

        if (hour === 12) {
            if (this.endTime.meridian === meridiansEnum.AM) {
                hour = 0;
            }
        } else {
            hour = this.endTime.meridian === meridiansEnum.PM ? hour + 12 : hour;
        }

        if (this.range.end) {
            this.range.end.setHours(hour);
            this.range.end.setMinutes(minute);
        } else {
            this.range.end = new Date();
            this.range.end.setHours(hour);
            this.range.end.setMinutes(minute);
            this.endDate = new Date(this.range.end);
        }

        this.onModelChange(this.range);
    }

    /**
     * Handles the timezone input value changing
     */
    timezoneChanged(): void {
        this.range.timezone = this.timezone;

        this.onModelChange(this.range);
    }
}
